import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import type { FollowUpTaskDto } from "@grizzly/shared";
import { taskPriority, taskStatus } from "@grizzly/shared";
import { api } from "../../lib/apiClient.js";
import type { ApiClientError } from "../../lib/apiClient.js";
import { useI18n, type MessageKey } from "../../i18n/index.js";
import { Badge } from "../ui/badge.js";
import { Button } from "../ui/button.js";
import { Card } from "../ui/card.js";
import { ErrorState, EmptyState } from "../ui/states.js";
import { useToast } from "../../lib/useToast.js";
import { formatDateTime } from "../../lib/format.js";
import { CheckCircle2, ClipboardCheck } from "lucide-react";

export function FollowUpQueue({ userId }: { userId: string }) {
  const { push } = useToast();
  const { t } = useI18n();
  const [tasks, setTasks] = useState<FollowUpTaskDto[]>([]);
  const [error, setError] = useState<{ message: string } | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      const res = await api.listTasks({ assignee: userId });
      setTasks(res.items.filter((task) => task.status !== "completed"));
    } catch (err) {
      const e = err as ApiClientError;
      setError({ message: e.message });
    }
  }, [userId]);

  useEffect(() => {
    void load();
  }, [load]);

  const complete = useCallback(
    async (task: FollowUpTaskDto) => {
      try {
        await api.completeTask(task.id);
        push(t("customers.tasks.completedToast"), task.subject);
        await load();
      } catch (err) {
        const e = err as ApiClientError;
        push(e.message, undefined, "danger");
      }
    },
    [load, push, t]
  );

  if (error) return <ErrorState message={error.message} />;

  return (
    <Card>
      <div className="mb-3 flex items-center justify-between">
        <h2 className="flex items-center gap-2 text-sm font-semibold text-ink">
          <ClipboardCheck className="h-4 w-4" aria-hidden="true" />
          {t("customers.followUps.title")}
        </h2>
        <span className="text-xs text-ink-muted">
          {t("customers.followUps.open", { count: tasks.length })}
        </span>
      </div>

      {tasks.length === 0 ? (
        <EmptyState
          title={t("customers.followUps.emptyTitle")}
          description={t("customers.followUps.emptyDescription")}
        />
      ) : (
        <ul className="space-y-2">
          {tasks.map((task) => (
            <li
              key={task.id}
              className="flex items-start justify-between gap-2 rounded border border-line px-3 py-2"
            >
              <div className="min-w-0">
                <p className="flex flex-wrap items-center gap-2 text-sm font-medium text-ink">
                  {task.subject}
                  <Badge tone={taskStatus(task.status).tone}>
                    {t(`status.task.${task.status}` as MessageKey)}
                  </Badge>
                  {task.priority === "high" ? (
                    <Badge tone={taskPriority(task.priority).tone}>
                      {t("status.priority.high")}
                    </Badge>
                  ) : null}
                </p>
                <p className="text-xs text-ink-muted">
                  <Link
                    to={`/customers/${task.customerId}`}
                    className="text-action-primary-text hover:underline"
                  >
                    {task.customerName}
                  </Link>
                  {task.dueAt
                    ? ` · ${t("customers.tasks.dueAt", { date: formatDateTime(task.dueAt) })}`
                    : ""}
                </p>
              </div>
              <Button size="sm" variant="secondary" onClick={() => void complete(task)}>
                <CheckCircle2 className="h-4 w-4" aria-hidden="true" />
                {t("common.done")}
              </Button>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}
